import * as React from 'react'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import { cn } from '@/lib/utils'

/**
 * Figma (nodes 18597:81811, 18597:81812 — Scroll Area):
 * Container: bg-background, border border-border, rounded-md (8px), overflow-clip
 * Scrollbar track: transparent, hover:bg-border/50
 * Scrollbar thumb: bg-border, rounded-full, hover:bg-border/80
 * Vertical scrollbar: w-2 (8px), p-[1px]
 * Horizontal scrollbar: h-2 (8px), p-[1px]
 */

export interface IntuiScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  /** Which scrollbar to render: "vertical", "horizontal", or "both" (default: "vertical") */
  orientation?: 'vertical' | 'horizontal' | 'both'
  /** Scrollbar visibility: "auto" hides when not needed, "always" keeps visible, "hover" shows on hover, "scroll" shows while scrolling */
  scrollbarVisibility?: 'auto' | 'always' | 'hover' | 'scroll'
}

const IntuiScrollArea = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.Root>,
  IntuiScrollAreaProps
>(
  (
    {
      className,
      children,
      orientation = 'vertical',
      scrollbarVisibility = 'hover',
      ...props
    },
    ref
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn('relative overflow-hidden', className)}
      type={scrollbarVisibility}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport className="h-full w-full rounded-[inherit]">
        {children}
      </ScrollAreaPrimitive.Viewport>
      {(orientation === 'vertical' || orientation === 'both') && (
        <IntuiScrollBar orientation="vertical" />
      )}
      {(orientation === 'horizontal' || orientation === 'both') && (
        <IntuiScrollBar orientation="horizontal" />
      )}
      {orientation === 'both' && <ScrollAreaPrimitive.Corner />}
    </ScrollAreaPrimitive.Root>
  )
)
IntuiScrollArea.displayName = 'IntuiScrollArea'

export interface IntuiScrollBarProps
  extends React.ComponentPropsWithoutRef<
    typeof ScrollAreaPrimitive.ScrollAreaScrollbar
  > {}

const IntuiScrollBar = React.forwardRef<
  React.ComponentRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  IntuiScrollBarProps
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' && 'h-full w-2 border-l border-l-transparent p-[1px]',
      orientation === 'horizontal' &&
        'h-2 flex-col border-t border-t-transparent p-[1px]',
      className
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
))
IntuiScrollBar.displayName = 'IntuiScrollBar'

export { IntuiScrollArea, IntuiScrollBar }
